import type { components } from '@social-studio/operator-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../../lib/api';
import { createPasskey } from '../../lib/webauthn';
import { ME_QUERY_KEY } from '../auth/useOperatorMe';

export type Passkey = components['schemas']['PasskeyDto'];

const PASSKEYS_KEY = ['operator-passkeys'] as const;

/** The own passkeys (T460-1-4); `enabled` is false where WebAuthn is missing. */
export function usePasskeys({ enabled = true }: { readonly enabled?: boolean } = {}) {
  return useQuery({ queryKey: PASSKEYS_KEY, queryFn: () => call(() => api.GET('/v1/operator/me/passkeys')), enabled });
}

/** Add (challenge, browser dialog, answer) and remove; both refresh the list and `me.passkeys`. */
export function usePasskeyWrites() {
  const queryClient = useQueryClient();
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY }), queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })]);
  return {
    add: useMutation({
      mutationFn: async (name: string) => {
        const { options } = await call(() => api.POST('/v1/operator/me/passkeys/options'));
        const response = await createPasskey(options);
        return call(() => api.POST('/v1/operator/me/passkeys', { body: { name, response: response as unknown as Record<string, unknown> } }));
      },
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => call(() => api.DELETE('/v1/operator/me/passkeys/{id}', { params: { path: { id } } })),
      onSuccess: refresh,
      onError: refresh,
    }),
  };
}
